import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  Grid,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import axios from "axios";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import UploadMultiFile from "../../../components/upload/UploadMultiFile";
import InnerUpload from "../../../shared/InnerUpload";
import OrderAttachments from "../../../shared/OrderAttachments";
import { Attachment } from "../../../shared/types/Attachment";
import { generateFullPaths } from "../../../taskManagement/documents/generateFullPaths";
import { toBase64 } from "../../../utils/helpers";
import { stripDataUriPrefix } from "../../../utils/stripDataUriPrefix";
import { ConcatenatedDocumentTypes, Document } from "./types";

export default function UploadModal({
  updateItems,
  open,
  handleClose,
  validationCheck,
  error,
  onSave,
  documentInfo,
  orderNumber,
  applicationInitiator = "",
  customHeaderText = "",
  customEditFunction,
  isConfirmEditAndDelete,
  setIsConfirmEditAndDelete,
  setPolicyNote,
  isUseNameOfFile,
  hasDocumentType,
  hasAdditionalDescription,
  isEditMode,
  policyNote
}: {
  updateItems: () => void;
  open: boolean;
  handleClose: () => void;
  hasDocumentType?: boolean;
  hasAdditionalDescription?: boolean;
  validationCheck?: () => Promise<boolean>;
  error?: string;
  onSave: (documentIds: number[], recordedDocIds?: number[]) => void;
  documentInfo: Document;
  orderNumber: string;
  isUseNameOfFile?: boolean;
  applicationInitiator: string;
  customHeaderText?: string;
  customEditFunction?: (base64: string, documentIds: number[]) => void;
  isConfirmEditAndDelete: boolean;
  setIsConfirmEditAndDelete: Dispatch<SetStateAction<boolean>>;
  setPolicyNote: (e: any) => void;
  isEditMode: boolean;
  policyNote: string | undefined;
}) {
  const [selectedAssociatedDocs, setSelectedAssociatedDocs] = useState<Attachment[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [concatenatedDocumentTypes, setConcatenatedDocumentTypes] = useState<ConcatenatedDocumentTypes[]>([]);
  const [selectedConcatenatedDocumentType, setSelectedConcatenatedDocumentType] =
    useState<ConcatenatedDocumentTypes | null>(null);
  const [additionalDescription, setAdditionalDescription] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [selectedAttachments, setSelectedAttachments] = useState<Attachment[]>([]);
  const [validationError, setValidationError] = useState(false);
  const getAndSetDocumentTypes = async (orderNumber: string) => {
    const { data } = await axios.get(`/api/taskmanagement/documents/getcategories?ordernumber=${orderNumber}`);
    const concatenatedDocumentTypes = generateFullPaths(data);
    setConcatenatedDocumentTypes(concatenatedDocumentTypes);
  };

  const handleFileDrop = useCallback(
    (acceptedFile: any) => {
      const file = Object.assign(acceptedFile[0], {
        preview: URL.createObjectURL(acceptedFile[0])
      });
      setFile(file);
    },
    [setFile]
  );
  const saveUploadToMomentum = async (document: Document, file: File) => {
    const description = [
      selectedConcatenatedDocumentType?.description || documentInfo.description || "",
      additionalDescription
    ]
      .filter(Boolean)
      .join(" ")
      .trim();

    const { data } = await axios.post<number>("/api/outstandingChecks/UploadDocument", {
      ...document,
      mTANumber: orderNumber,
      base64File: stripDataUriPrefix(await toBase64(file)),
      fileName: isUseNameOfFile ? file?.name : documentInfo.fileName,
      subcategoryId: selectedConcatenatedDocumentType?.subcategoryId || documentInfo.subcategoryId,
      description: description,
      // descriptionAdditional: additionalDescription,
      applicationInitiator
    });
    return data;
  };

  const save = async () => {
    setSubmitting(true);
    const documents = selectedAttachments?.map((d) => d.id) || [];
    const associatedDocs = selectedAssociatedDocs?.map((d) => d.id) || [];
    let isValid = true;
    if (validationCheck) {
      isValid = await validationCheck();
    }
    if (!isValid) {
      setValidationError(true);
      setSubmitting(false);
      return;
    }
    if (customEditFunction !== undefined && isConfirmEditAndDelete && file) {
      const base64File = stripDataUriPrefix(await toBase64(file));
      await customEditFunction(base64File, []);
      setSubmitting(false);
      updateItems();
      handleClose();
      return;
    }
    if (file && !isConfirmEditAndDelete && (!hasDocumentType || selectedConcatenatedDocumentType)) {
      const uploadedDocumentId = await saveUploadToMomentum(documentInfo, file);
      documents.push(uploadedDocumentId);
    }
    if (!documents.length && !associatedDocs.length) return;
    if (!isConfirmEditAndDelete) {
      onSave(documents, associatedDocs);
    }
    setSubmitting(false);
    updateItems();
    handleClose();
  };

  useEffect(() => {
    if (hasDocumentType && orderNumber) {
      getAndSetDocumentTypes(orderNumber);
    }
  }, [orderNumber]);
  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          return;
        }
        handleClose();
      }}
      fullWidth
      maxWidth="md"
    >
      <DialogContent sx={{ paddingBottom: 3, paddingTop: 4 }}>
        <Stack direction="column">
          <Typography variant="subtitle1">Upload</Typography>
        </Stack>
        <Stack sx={{ paddingBottom: 3 }}>
          <Stack direction="row" spacing={2} paddingTop={1}>
            <Card sx={{ width: "100%" }}>
              <CardContent>
                <Stack direction="column" justifyContent="space-between" spacing={2}>
                  {hasDocumentType && (
                    <Autocomplete
                      fullWidth
                      onChange={(e, value) => setSelectedConcatenatedDocumentType(value)}
                      options={concatenatedDocumentTypes}
                      getOptionLabel={(option) => option.fullPath || ""}
                      renderInput={(params) => (
                        <TextField {...params} label="Document Type" placeholder="Start typing..." />
                      )}
                    />
                  )}
                  {hasAdditionalDescription && (
                    <TextField
                      label="Additional Description"
                      onChange={(e) => setAdditionalDescription(e.target.value)}
                    />
                  )}
                  <UploadMultiFile
                    files={file ? [file] : []}
                    onDrop={handleFileDrop}
                    onRemove={() => setFile(null)}
                    onRemoveAll={() => setFile(null)}
                    showPreview={false}
                    showSelectedFiles={true}
                    multiple={false}
                  />
                  {selectedConcatenatedDocumentType && (
                    <Stack direction="column" justifyContent="right">
                      <Box>
                        <Typography variant="body2">Uploaded document will be saved to</Typography>
                        <Typography variant="subtitle2">{`${selectedConcatenatedDocumentType.fullPath} ${
                          additionalDescription ? `- ${additionalDescription}` : ""
                        }`}</Typography>
                      </Box>
                    </Stack>
                  )}
                </Stack>
              </CardContent>
            </Card>
            <Card sx={{ width: "100%" }}>
              <CardContent>
                <Typography variant="body2" marginBottom={1}>
                  Or <strong>Choose a document from Momentum</strong>
                </Typography>
                <OrderAttachments
                  selectedAttachments={selectedAttachments}
                  setSelectedAttachments={setSelectedAttachments}
                  orderNumber={orderNumber}
                />
              </CardContent>
            </Card>
          </Stack>
        </Stack>
        {isEditMode && (
          <Grid item md={2} xs={12} sx={{ display: "flex", marginBottom:2 }}>
            <FormControlLabel
              control={
                <Checkbox checked={isConfirmEditAndDelete} onChange={(e, value) => setIsConfirmEditAndDelete(value)} />
              }
              label="Do you wish to delete the previously uploaded policy from Momentum (home page)?"
            />
          </Grid>
        )}
        <Grid container spacing={2} marginBottom={3}>
          <Grid item xs={12}>
            <TextField
              label={isEditMode ? "Edit Note" : "Include Note with Policy Transmission"}
              multiline
              rows={3}
              variant="outlined"
              fullWidth
              onChange={(e) => setPolicyNote(e.target.value)}
              value={isEditMode ? policyNote : undefined}
            />
          </Grid>
        </Grid>
        <Card sx={{ width: "50%" }}>
          <CardContent>
            <Typography variant="body2" marginBottom={1}>
              <strong>Choose a Recorded document from Momentum</strong>
            </Typography>
            <OrderAttachments
              selectedAttachments={selectedAssociatedDocs}
              setSelectedAttachments={setSelectedAssociatedDocs}
              orderNumber={orderNumber}
            />
          </CardContent>
        </Card>
        <Stack direction={"row"} justifyContent="flex-end" spacing={2} marginTop={3}>
          <Button variant="outlined" onClick={handleClose} color="inherit">
            Cancel
          </Button>
          <Button variant="contained" disabled={!file && selectedAttachments.length === 0 && selectedAssociatedDocs.length === 0} onClick={save}>
            {submitting ? "Saving...." : "Save"}
          </Button>
          {validationError && <Typography color="red">{error}</Typography>}
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
